import React, { useState, useContext } from "react";
import { FiMessageSquare } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { postLead } from "../services/Api";
import { GlobalContext } from "../GlobalContext";
import axiosInstance from "../services/BaseUrl";
import { useTranslation } from "react-i18next";

export default function ContactForm({
  propertyTitle = "",
  propertySlug = "",
  developerName = "",
  customMessage = "",
  isPlaceholderMessageEnabled = true,  // Flag to control the placeholder message
}) {
  const { t } = useTranslation();  // Hook to access translations
  const navigate = useNavigate();
  const { addLead } = useContext(GlobalContext);

  // If the flag is false, do not use the default placeholder message
  const defaultMessage = isPlaceholderMessageEnabled && customMessage === ""
    ? `${t('contact_form.default_message')} ${propertyTitle}. ${t('contact_form.please_contact_me')}`
    : customMessage || ""; // Use customMessage if provided or leave empty

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    message: defaultMessage,  // Use the determined message
    slug: propertySlug,
    developer_name: developerName,
  });

  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axiosInstance.post("/contact-us/", form, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.data.success) {
        const leadRes = await postLead({
          full_name: form.name,
          contact_number: form.phone,
          lead_type: "email",
          inquiry_link: window.location.href,
        });

        if (leadRes.success && leadRes.data) {
          addLead(leadRes.data);
        }

        setForm({
          name: "",
          email: "",
          phone: "",
          role: "",
          message: "",
          slug: "",
          developer_name: "",
        });

        navigate("/thank-you");
      } else {
        alert(res.data.message || t('contact_form.failed_message'));
      }
    } catch (err) {
      console.error(err);
      alert(t('contact_form.error_message'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-lg p-6 space-y-4"
      >
        <h4 className="text-lg font-semibold text-gray-800">{t('contact_form.get_in_touch')}</h4>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('contact_form.name')}*</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('contact_form.email')}*</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('contact_form.phone')}</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('contact_form.select_role')}</label>
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
          >
            <option value="">{t('contact_form.select_role_placeholder')}</option>
            <option value="Agent">{t('contact_form.agent')}</option>
            <option value="Buyer">{t('contact_form.buyer')}</option>
            <option value="Just Exploring">{t('contact_form.just_exploring')}</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('contact_form.message')}*</label>
          <div className="relative">
            <textarea
              name="message"
              rows="4"
              value={form.message}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none"
            />
            <FiMessageSquare className="absolute bottom-2 right-2 text-gray-400" />
          </div>
        </div>

        <button
          type="submit"
          disabled={sending}
          className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-full py-2 font-medium hover:from-teal-500 hover:to-cyan-600 transition disabled:opacity-50"
        >
          {sending ? t('contact_form.sending') : t('contact_form.send_message')}
        </button>
      </form>
    </div>
  );
}
